import * as React from "react";
import { Button } from "../ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { X, ArrowLeft, Search, FilePlus, FolderPlus, Loader } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useRequestGallery } from "./RequestGalleryContext";
import { cn } from "@/lib/utils";
import { Request } from "@/types/research.types";

const CreateRequestForm = React.lazy(() =>
  import("./CreateRequest").then((module) => ({ default: module.CreateRequestForm }))
);
const CreateRequestCategoryForm = React.lazy(() =>
  import("./CreateRequestCategory").then((module) => ({ default: module.CreateRequestCategoryForm }))
);
const EditRequestCategoryForm = React.lazy(() =>
  import("./EditRequestCategory").then((module) => ({ default: module.EditRequestCategoryForm }))
);
const EditRequestForm = React.lazy(() =>
  import("./EditRequest").then((module) => ({ default: module.EditRequestForm }))
);
const RequestCategoryList = React.lazy(() => import("./RequestCategoryList"));
const RequestList = React.lazy(() => import("./RequestList"));
import ExecuteAutomationDialog from "./ExecuteAutomationDialog";



const modeTitles: { [key: string]: string } = {
  "create-category": "Add Category",
  "create-request": "Add Request",
  "edit-category": "Edit Category",
  "edit-request": "Edit Request",
};

const RequestGalleryContent: React.FC = () => {
  const {
    mode,
    handleBack,
    selectedCategoryId,
    currentParentId,
    handleBackToCategories,
    handleBackToParentCategory,
    selectedCategory,
    currentCategory,
    setMode,
    searchQuery,
    setSearchQuery,
    editingCategory,
    editingRequest,
    onSelect,
    closeGallery,
    type,
  } = useRequestGallery();

  const [isExecuteDialogOpen, setIsExecuteDialogOpen] = React.useState(false);
  const [executingRequest, setExecutingRequest] = React.useState<Request | null>(null);

  const handleRequestClick = (content: string, request?: Request) => {
    if (request?.chain_with && request.chain_with.length > 0) {
      setExecutingRequest(request);
      setIsExecuteDialogOpen(true);
    } else if (onSelect) {
      onSelect(content, request);
      closeGallery();
    }
  };

  const confirmExecuteAutomation = () => {
    if (executingRequest && onSelect) {
      onSelect(executingRequest.content, executingRequest);
      setExecutingRequest(null);
      setIsExecuteDialogOpen(false);
      closeGallery();
    }
  };

  const handleClose = () => {
    closeGallery();
  };

  return (
    <div className="rounded-md flex flex-col h-full">
      <div className="p-2 border-b flex items-center justify-between gap-2">
        {mode !== "list" ? (
          <>
            <Button variant="ghost" size="icon" className="p-0 h-auto w-auto" onClick={handleBack}>
              <ArrowLeft className="size-3.5" />
            </Button>
            <h3 className="text-sm font-semibold flex-1 truncate">{modeTitles[mode]}</h3>
          </>
        ) : (
          <>
            {(selectedCategoryId || currentParentId) && (
              <Button
                variant="ghost"
                size="icon"
                className="p-0 h-auto w-auto"
                onClick={selectedCategoryId ? handleBackToCategories : handleBackToParentCategory}
              >
                <ArrowLeft className="size-3.5" />
              </Button>
            )}
            <h3 className="text-sm font-semibold flex-1 truncate">
              {selectedCategory
                ? selectedCategory.name
                : currentCategory
                  ? currentCategory.name
                  : "Request Gallery"}
            </h3>
            <div className="flex gap-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger onClick={() => setMode("create-category")}>
                    <FolderPlus className="size-3.5" />
                  </TooltipTrigger>
                  <TooltipContent>Add Category</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger onClick={() => setMode("create-request")}>
                    <FilePlus className="size-3.5" />
                  </TooltipTrigger>
                  <TooltipContent>Add Request</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </>
        )}
        <Button variant="ghost" size="icon" onClick={handleClose}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      {mode === "list" && (
        <div className="p-2 border-b">
          <InputGroup>
            <InputGroupAddon>
              <Search className="size-3.5 text-neutral-500" />
            </InputGroupAddon>
            <InputGroupInput
              placeholder={selectedCategoryId ? "Search requests..." : "Search categories..."}
              value={searchQuery}
              className="placeholder:text-neutral-500"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </div>
      )}

      <div
        className={cn(
          mode === "list"
            ? "flex-1 overflow-auto flex flex-col p-2 gap-2"
            : "flex-1 flex flex-col overflow-hidden p-0"
        )}
      >
        <React.Suspense
          fallback={
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Loader className="animate-spin" size={48} />
              <p>Loading...</p>
            </div>
          }
        >
          {mode === "list" ? (
            selectedCategoryId ? (
              <RequestList onRequestSelect={handleRequestClick} />
            ) : (
              <RequestCategoryList onRequestSelect={handleRequestClick} />
            )
          ) : mode === "create-category" ? (
            <CreateRequestCategoryForm />
          ) : mode === "create-request" ? (
            <CreateRequestForm />
          ) : mode === "edit-category" && editingCategory ? (
            <EditRequestCategoryForm />
          ) : mode === "edit-request" && editingRequest ? (
            <EditRequestForm request={editingRequest} onSuccess={handleBack} onCancel={handleBack} type={type} />
          ) : null}
        </React.Suspense>
      </div>

      <ExecuteAutomationDialog
        isOpen={isExecuteDialogOpen}
        onClose={() => setIsExecuteDialogOpen(false)}
        onConfirm={confirmExecuteAutomation}
        request={executingRequest}
      />
    </div>
  );
};

const RequestGallery: React.FC<{ children: React.ReactNode; id: string; type?: "discuss" | "research" }> = ({
  children,
  id,
  type,
}) => {
  const { activeGalleryId, closeGallery, setType } = useRequestGallery();
  const isOpen = activeGalleryId === id;

  React.useEffect(() => {
    if (isOpen && type) {
      setType(type);
    }
  }, [isOpen, type, setType]);

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && closeGallery()}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[calc(100vw-2rem)] h-[500px]" side="top" align="start">
        <RequestGalleryContent />
      </PopoverContent>
    </Popover>
  );
};

export default RequestGallery;
